import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarCheck, Camera, CreditCard, Eye, MessageCircle, Pencil, Printer, Receipt, RefreshCcw, UserCheck, Wallet } from "lucide-react";
import { PageHeader, StatCard, StatusPill, EmptyState } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, inr, formatDate, todayISO } from "../lib/api";
import IdCardModal from "../components/IdCardModal";
import { buildWhatsAppLink } from "../components/WhatsAppBubble";

export default function StudentDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");
  const [renewOpen, setRenewOpen] = useState(false);
  const [idCardOpen, setIdCardOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [aadhaar, setAadhaar] = useState(null);
  const [library, setLibrary] = useState(null);
  const [renewForm, setRenewForm] = useState({ ending_date: "", monthly_fee: "", payment_amount: 0, payment_mode: "CASH" });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadPhoto = async (student) => {
    if (!student?.photo_path) {
      setPhotoUrl(null);
      return;
    }
    try {
      const response = await api.get(`/students/${student.id}/photo`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      setPhotoUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return url;
      });
    } catch (err) {
      setPhotoUrl(null);
    }
  };

  const load = async () => {
    try {
      const res = await api.get(`/students/${id}`);
      setData(res.data);
      setRenewForm((prev) => ({ ...prev, monthly_fee: res.data.data.monthly_fee || "", ending_date: res.data.data.ending_date || "" }));
      loadPhoto(res.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setError("Image must be under 3 MB");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    setError("");
    try {
      await api.post(`/students/${student.id}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  useEffect(() => {
    load();
    if (user.role !== "MAIN_ADMIN") {
      api.get("/library/profile").then((res) => setLibrary(res.data.data)).catch(() => {});
    }
  }, [id, user.role]);

  const student = data?.data;
  const payments = data?.payments || [];
  const bills = data?.bills || [];
  const attendance = data?.attendance || [];
  const bookIssues = data?.book_issues || [];

  const totals = useMemo(() => {
    const paid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const outstanding = bills.reduce((sum, b) => sum + Number(b.balance || 0), 0);
    const overdue = bills.filter((b) => b.status === "OVERDUE").length;
    return { paid, outstanding, overdue };
  }, [payments, bills]);

  const timeline = useMemo(() => {
    const events = [];
    payments.forEach((payment) => events.push({ id: `p-${payment.id}`, kind: "PAYMENT", when: payment.created_at, label: `Payment ${inr(payment.amount)} via ${payment.payment_mode}`, meta: payment.receipt_number }));
    bills.forEach((bill) => events.push({ id: `b-${bill.id}`, kind: "BILL", when: bill.created_at, label: `${bill.period_label} bill (${bill.status})`, meta: inr(bill.amount) }));
    bookIssues.forEach((issue) => events.push({ id: `i-${issue.id}`, kind: "BOOK", when: issue.created_at, label: `${issue.status === "ISSUED" ? "Issued" : "Returned"}: ${issue.book_name}`, meta: formatDate(issue.issue_date) }));
    attendance.slice(0, 10).forEach((att) => events.push({ id: `a-${att.id}`, kind: "ATTENDANCE", when: att.created_at, label: `Marked ${att.status.toLowerCase()}`, meta: formatDate(att.date) }));
    return events.sort((a, b) => (b.when || "").localeCompare(a.when || "")).slice(0, 15);
  }, [payments, bills, attendance, bookIssues]);

  const renewSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...renewForm, payment_amount: Number(renewForm.payment_amount || 0), monthly_fee: renewForm.monthly_fee ? Number(renewForm.monthly_fee) : undefined };
      await api.post(`/students/${student.id}/renew`, payload);
      setRenewOpen(false);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const viewAadhaar = async () => {
    try {
      const res = await api.get(`/students/${student.id}/aadhaar`);
      setAadhaar(res.data.data.aadhaar_number || "Not on file");
    } catch (err) {
      setError(errorText(err));
    }
  };

  const openEdit = () => {
    setEditForm({
      name: student.name || "",
      mobile: student.mobile || "",
      email: student.email || "",
      father_name: student.father_name || "",
      mother_name: student.mother_name || "",
      emergency_contact: student.emergency_contact || "",
      address: student.address || "",
      city: student.city || "",
      state: student.state || "",
      pincode: student.pincode || "",
      monthly_fee: student.monthly_fee || 0,
      notes: student.notes || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...editForm, monthly_fee: Number(editForm.monthly_fee || 0) };
      if (!payload.email) delete payload.email;
      await api.patch(`/students/${student.id}`, payload);
      setEditOpen(false);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  if (error) return <div className="error-message" data-testid="student-detail-error">{error}</div>;
  if (!student) return <div className="loading-screen"><span>Loading student…</span></div>;

  return (
    <div className="page-enter">
      <Link to="/students" className="back-link" data-testid="back-to-students"><ArrowLeft size={16} /> Back to students</Link>
      <PageHeader
        eyebrow="STUDENT RECORD"
        title={student.name}
        description={`${student.student_id} · joined ${formatDate(student.joining_date)} · ending ${formatDate(student.ending_date)}`}
        action={
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a
              className="button button-whatsapp"
              href={buildWhatsAppLink(
                student.mobile,
                `Hi ${student.name}, this is a friendly reminder from ${library?.name || "your library"}. Your membership ends on ${formatDate(student.ending_date)}. Please renew to continue your seat.\n\nOutstanding: ${inr(totals.outstanding)}\n\nThanks!`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-renewal-reminder"
            >
              <MessageCircle size={16} /> WhatsApp reminder
            </a>
            <button className="button button-outline" onClick={openEdit} data-testid="edit-student-button">
              <Pencil size={16} /> Edit profile
            </button>
            <button className="button button-outline" onClick={() => setIdCardOpen(true)} data-testid="print-id-card-button">
              <Printer size={16} /> ID card
            </button>
            <button className="button button-primary" onClick={() => setRenewOpen(true)} data-testid="renew-student-button">
              <RefreshCcw size={16} /> Renew membership
            </button>
          </div>
        }
      />

      <div className="stat-grid">
        <StatCard label="Total paid" value={inr(totals.paid)} detail={`${payments.length} payments`} icon={<Wallet size={18} />} tone="green" />
        <StatCard label="Outstanding" value={inr(totals.outstanding)} detail={`${totals.overdue} overdue bills`} icon={<CreditCard size={18} />} tone="orange" />
        <StatCard label="Attendance" value={attendance.filter((a) => a.status === "PRESENT").length} detail={`${attendance.length} records`} icon={<CalendarCheck size={18} />} />
        <StatCard label="Books" value={bookIssues.length} detail={`${bookIssues.filter((i) => i.status === "ISSUED").length} currently out`} tone="dark" icon={<Receipt size={18} />} />
      </div>

      <section className="content-card" style={{ marginBottom: 16 }}>
        <div className="card-heading"><div><span className="card-kicker">PROFILE</span><h2>Personal details</h2></div><StatusPill status={student.status} /></div>
        <div className="profile-top">
          <div className="profile-photo" data-testid="student-photo-tile">
            {photoUrl ? <img src={photoUrl} alt={student.name} /> : <span className="profile-photo-initial">{(student.name || "?").charAt(0).toUpperCase()}</span>}
            <label className={`profile-photo-upload ${uploading ? "disabled" : ""}`}>
              <Camera size={14} /> {uploading ? "Uploading…" : photoUrl ? "Replace photo" : "Add photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadPhoto} disabled={uploading} data-testid="student-photo-input" />
            </label>
          </div>
          <div className="profile-grid">
          <div><span>Mobile</span><strong>{student.mobile}</strong></div>
          <div><span>Email</span><strong>{student.email || "—"}</strong></div>
          <div><span>Father/Guardian</span><strong>{student.father_name || "—"}</strong></div>
          <div><span>Address</span><strong>{student.address || "—"}</strong></div>
          <div><span>City</span><strong>{student.city || "—"}</strong></div>
          <div><span>Monthly fee</span><strong>{inr(student.monthly_fee)}</strong></div>
          <div>
            <span>Aadhaar</span>
            <strong style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {aadhaar || student.aadhaar_masked || "—"}
              {!aadhaar && student.aadhaar_masked && user.role !== "STAFF" ? (
                <button type="button" className="icon-button" style={{ minWidth: 32, minHeight: 32 }} onClick={viewAadhaar} aria-label="View Aadhaar" data-testid="view-aadhaar-button">
                  <Eye size={14} />
                </button>
              ) : null}
            </strong>
          </div>
          </div>
        </div>
      </section>

      <div className="tabs" data-testid="student-detail-tabs">
        <button className={`tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")} data-testid="tab-overview">Timeline</button>
        <button className={`tab ${tab === "payments" ? "active" : ""}`} onClick={() => setTab("payments")} data-testid="tab-payments">Payments ({payments.length})</button>
        <button className={`tab ${tab === "bills" ? "active" : ""}`} onClick={() => setTab("bills")} data-testid="tab-bills">Bills ({bills.length})</button>
        <button className={`tab ${tab === "attendance" ? "active" : ""}`} onClick={() => setTab("attendance")} data-testid="tab-attendance">Attendance ({attendance.length})</button>
        <button className={`tab ${tab === "books" ? "active" : ""}`} onClick={() => setTab("books")} data-testid="tab-books">Books ({bookIssues.length})</button>
      </div>

      <section className="content-card">
        {tab === "overview" ? (
          timeline.length ? (
            <ul className="timeline" data-testid="student-timeline">
              {timeline.map((event) => (
                <li key={event.id} className={`timeline-item ${event.kind.toLowerCase()}`}>
                  <span className="timeline-dot" />
                  <div>
                    <strong>{event.label}</strong>
                    <small>{event.meta} · {formatDate(event.when)}</small>
                  </div>
                </li>
              ))}
            </ul>
          ) : <EmptyState icon={<UserCheck size={22} />} title="No activity yet" hint="Payments, bills and attendance will populate this timeline." />
        ) : null}

        {tab === "payments" ? (
          <div className="table-wrap">
            <table data-testid="student-payments-table">
              <thead><tr><th>Receipt</th><th>Date</th><th>Amount</th><th>Mode</th><th>For</th></tr></thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}><td>{payment.receipt_number}</td><td>{formatDate(payment.payment_date)}</td><td>{inr(payment.amount)}</td><td>{payment.payment_mode}</td><td>{payment.payment_for}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {tab === "bills" ? (
          <div className="table-wrap">
            <table data-testid="student-bills-table">
              <thead><tr><th>Period</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Due</th><th>Status</th></tr></thead>
              <tbody>
                {bills.map((bill) => (
                  <tr key={bill.id}><td>{bill.period_label}</td><td>{inr(bill.amount)}</td><td>{inr(bill.paid_amount)}</td><td>{inr(bill.balance)}</td><td>{formatDate(bill.due_date)}</td><td><StatusPill status={bill.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {tab === "attendance" ? (
          <div className="table-wrap">
            <table data-testid="student-attendance-table">
              <thead><tr><th>Date</th><th>Status</th><th>Check-in</th></tr></thead>
              <tbody>
                {attendance.map((record) => (
                  <tr key={record.id}><td>{formatDate(record.date)}</td><td><StatusPill status={record.status} /></td><td>{formatDate(record.check_in)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {tab === "books" ? (
          <div className="table-wrap">
            <table data-testid="student-books-table">
              <thead><tr><th>Book</th><th>Issued</th><th>Expected return</th><th>Status</th></tr></thead>
              <tbody>
                {bookIssues.map((issue) => (
                  <tr key={issue.id}><td>{issue.book_name}</td><td>{formatDate(issue.issue_date)}</td><td>{formatDate(issue.expected_return_date)}</td><td><StatusPill status={issue.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {renewOpen ? (
        <Modal title="Renew membership" eyebrow="EXTEND VALIDITY" close={() => setRenewOpen(false)} testId="renew-modal">
          <form className="modal-form" onSubmit={renewSubmit}>
            <label>New ending date<input type="date" required value={renewForm.ending_date} onChange={(event) => setRenewForm({ ...renewForm, ending_date: event.target.value })} data-testid="renew-end-date" /></label>
            <label>Monthly fee (optional override)<input type="number" min="0" value={renewForm.monthly_fee} onChange={(event) => setRenewForm({ ...renewForm, monthly_fee: event.target.value })} /></label>
            <div className="form-split">
              <label>Payment amount<input type="number" min="0" value={renewForm.payment_amount} onChange={(event) => setRenewForm({ ...renewForm, payment_amount: event.target.value })} data-testid="renew-payment-amount" /></label>
              <label>Mode
                <select value={renewForm.payment_mode} onChange={(event) => setRenewForm({ ...renewForm, payment_mode: event.target.value })}>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="BANK_TRANSFER">Bank transfer</option>
                  <option value="CARD">Card</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setRenewOpen(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-renew-button">Renew <ArrowRight size={16} /></button>
            </div>
          </form>
        </Modal>
      ) : null}

      {idCardOpen ? <IdCardModal student={student} library={library} photoUrl={photoUrl} close={() => setIdCardOpen(false)} /> : null}

      {editOpen ? (
        <Modal title="Edit profile" eyebrow="UPDATE STUDENT" close={() => setEditOpen(false)} testId="edit-student-modal" wide>
          <form className="modal-form grid-form" onSubmit={submitEdit}>
            <label>Full name<input type="text" required value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} data-testid="edit-student-name-input" /></label>
            <label>Mobile<input type="tel" required value={editForm.mobile} onChange={(event) => setEditForm({ ...editForm, mobile: event.target.value })} data-testid="edit-student-mobile-input" /></label>
            <label>Email<input type="email" value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} /></label>
            <label>Monthly fee<input type="number" min="0" value={editForm.monthly_fee} onChange={(event) => setEditForm({ ...editForm, monthly_fee: event.target.value })} data-testid="edit-student-fee-input" /></label>
            <label>Father / guardian<input type="text" value={editForm.father_name} onChange={(event) => setEditForm({ ...editForm, father_name: event.target.value })} /></label>
            <label>Mother name<input type="text" value={editForm.mother_name} onChange={(event) => setEditForm({ ...editForm, mother_name: event.target.value })} /></label>
            <label>Emergency contact<input type="tel" value={editForm.emergency_contact} onChange={(event) => setEditForm({ ...editForm, emergency_contact: event.target.value })} /></label>
            <label>City<input type="text" value={editForm.city} onChange={(event) => setEditForm({ ...editForm, city: event.target.value })} /></label>
            <label>State<input type="text" value={editForm.state} onChange={(event) => setEditForm({ ...editForm, state: event.target.value })} /></label>
            <label>Pincode<input type="text" value={editForm.pincode} onChange={(event) => setEditForm({ ...editForm, pincode: event.target.value })} /></label>
            <label className="grid-span-2">Address<input type="text" value={editForm.address} onChange={(event) => setEditForm({ ...editForm, address: event.target.value })} /></label>
            <label className="grid-span-2">Notes<input type="text" value={editForm.notes} onChange={(event) => setEditForm({ ...editForm, notes: event.target.value })} /></label>
            <div className="modal-actions grid-span-2">
              <button type="button" className="button button-outline" onClick={() => setEditOpen(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-edit-student-button">Save changes <ArrowRight size={16} /></button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
